import type { Note } from '@just-us/shared'
/**
 * pages/note-detail.tsx
 *
 * Full note view.
 * - Mood-themed background
 * - Calls PATCH /notes/:id/open on mount (if not yet opened, and caller is receiver)
 * - Note body in Caveat font (.text-note)
 * - Doodle <img> if present
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import BottomNav from '../components/bottom-nav'
import { LockedCountdown } from '../components/locked-countdown'
import { useCouple } from '../hooks/use-couple'
import { useNotes, useOpenNote } from '../hooks/use-notes'
import { getAuthPayload } from '../store/auth'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (unix: number): string =>
  new Date(unix * 1000).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

const MOOD_BG: Record<string, string> = {
  lovey: 'rgba(244, 192, 209, 0.15)',
  celebratory: 'rgba(196, 181, 232, 0.15)',
  dreamy: 'rgba(250, 248, 244, 0.9)',
  cozy: 'rgba(245, 198, 160, 0.15)',
}

const MOOD_ACCENT: Record<string, string> = {
  lovey: '#D4537E',
  celebratory: '#7C5CBF',
  dreamy: '#B89AA2',
  cozy: '#D4823A',
}

const MOOD_EMOJI: Record<string, string> = {
  lovey: '💕',
  celebratory: '🎉',
  dreamy: '🌙',
  cozy: '☕',
}

const ENVELOPE_EMOJI: Record<string, string> = {
  plain: '✉️',
  heart: '💌',
  flower: '🌸',
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function NoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const payload = getAuthPayload()

  const { data: inboxData } = useNotes('inbox')
  const { data: sentData } = useNotes('sent')
  const { data: coupleData } = useCouple()

  const openNote = useOpenNote()

  const [revealed, setRevealed] = useState(false)

  const note: Note | undefined =
    inboxData?.data.find((n) => n.id === id) ?? sentData?.data.find((n) => n.id === id)

  const senderNickname =
    coupleData?.data?.users.find((u) => u.id === note?.sender_id)?.nickname ?? 'your partner'

  const isReceiver = note ? note.sender_id !== payload?.user_id : false
  const isLocked = !!(note?.unlock_at && Math.floor(Date.now() / 1000) < note.unlock_at)

  useEffect(() => {
    if (!note || !isReceiver || note.opened_at || isLocked) return
    openNote.mutate(note.id, {
      onSettled: () => {
        setTimeout(() => setRevealed(true), 80)
      },
    })
  }, [note?.id, isReceiver, note?.opened_at, isLocked]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (note && isReceiver && note.opened_at && !revealed) {
      setRevealed(true)
    }
  }, [note?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading / not found ──
  if (!note) {
    return (
      <div className="min-h-screen bg-[#FAF8F4] flex flex-col items-center justify-center max-w-[430px] mx-auto">
        <div className="text-[40px] animate-float">✉️</div>
        <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] mt-3">Loading…</p>
      </div>
    )
  }

  const moodBg = note.mood ? MOOD_BG[note.mood] : 'transparent'
  const moodAccent = note.mood ? MOOD_ACCENT[note.mood] : '#D4537E'
  const moodEmoji = note.mood ? MOOD_EMOJI[note.mood] : null

  return (
    <div
      className="min-h-screen flex flex-col max-w-[430px] mx-auto relative"
      style={{ background: `linear-gradient(180deg, ${moodBg} 0%, #FAF8F4 60%)` }}
    >
      {/* Sticky header */}
      <header
        className="sticky top-0 z-40 pt-[env(safe-area-inset-top)]"
        style={{ background: 'transparent' }}
      >
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-[36px] h-[36px] rounded-full bg-white/70 backdrop-blur-sm border border-[#F4C0D1] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          {moodEmoji && <span className="text-[22px] animate-bounce-in">{moodEmoji}</span>}
          <div className="w-[36px] h-[36px] rounded-full bg-white/70 backdrop-blur-sm border border-[#F4C0D1] flex items-center justify-center text-[18px]">
            {ENVELOPE_EMOJI[note.envelope_style] ?? '✉️'}
          </div>
        </div>
      </header>

      {/* Scrollable content */}
      <main className="flex-1 overflow-y-auto px-5 pb-[88px] pt-2">
        <div className="animate-fade-in-up flex flex-col gap-5">
          {/* Sender + date */}
          <div className="flex items-center gap-3">
            <div
              className="w-[44px] h-[44px] rounded-full bg-white border-2 flex items-center justify-center text-[22px] shadow-[0_2px_8px_rgba(212,83,126,0.1)]"
              style={{ borderColor: moodAccent }}
            >
              💕
            </div>
            <div>
              <p className="font-['DM_Sans'] text-[13px] font-medium text-[#2C1810]">
                {isReceiver ? `From ${senderNickname}` : 'You wrote'}
              </p>
              <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">
                {formatDate(note.created_at)}
              </p>
            </div>
          </div>

          {/* Locked state */}
          {isLocked ? (
            <div className="bg-white rounded-[16px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] p-6 text-center">
              <div className="text-[40px] mb-3">🔒</div>
              <p className="font-['Playfair_Display'] text-[17px] font-medium text-[#2C1810] mb-1">
                Time-locked
              </p>
              <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2]">
                Opens in <LockedCountdown unlockAt={note.unlock_at!} />
              </p>
            </div>
          ) : (
            <div className={isReceiver ? (revealed ? 'animate-envelope-open' : 'opacity-0') : ''}>
              {note.content && (
                <div
                  className="bg-white rounded-[16px] border shadow-[0_2px_12px_rgba(212,83,126,0.08)] p-5 animate-fade-in-up"
                  style={{ borderColor: moodAccent + '40' }}
                >
                  <p className="text-note whitespace-pre-wrap">{note.content}</p>
                </div>
              )}

              {note.doodle_url && (
                <div className="rounded-[16px] overflow-hidden border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] animate-fade-in-up [animation-delay:80ms]">
                  <img
                    src={note.doodle_url}
                    alt="Doodle"
                    className="w-full object-contain bg-[#FAF8F4]"
                  />
                </div>
              )}

              {!isReceiver && (
                <div className="flex items-center gap-2">
                  {note.opened_at ? (
                    <>
                      <span className="font-['DM_Sans'] text-[12px] text-[#D4537E] font-medium">
                        ✓✓
                      </span>
                      <span className="font-['DM_Sans'] text-[12px] text-[#D4537E]">
                        Read {formatDate(note.opened_at)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">✓</span>
                      <span className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">Delivered</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  )
}
